import React from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function VehicleCard({ 
  title, 
  journeys, 
  selectedJourneys, 
  onToggleJourney, 
  onDelete, 
  onCapacityChange,
  capacityValue 
}) {
  return (
    <Card
      sx={{
        width: "100%",
        mb: 2,
        boxShadow: ({ boxShadows: { md } }) => md,
        transition: "0.2s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 6 },
      }}
    >
      <MDBox p={2}>
        {/* Header: Icon, Title and Capacity */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon fontSize="medium" color="info">directions_bus</Icon>
            <MDTypography variant="h6" fontWeight="medium" textTransform="uppercase">
              {title}
            </MDTypography>
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={1}>
            <Tooltip title="Vehicle Capacity" placement="top">
              <TextField
                type="number"
                size="small"
                label="Capacity"
                value={capacityValue || ""}
                onChange={(e) => onCapacityChange(e.target.value)}
                sx={{ width: "80px" }}
                inputProps={{ min: 1, max: 100 }}
              />
            </Tooltip>
            
            <IconButton color="error" onClick={onDelete} size="small">
              <Icon>delete</Icon>
            </IconButton>
          </MDBox>
        </MDBox>

        <Divider sx={{ my: 1.5 }} />

        {/* Body: Journey Selection */}
        <MDBox>
          <MDTypography variant="caption" color="text" fontWeight="bold" textTransform="uppercase" mb={1} display="block">
            Select Active Route
          </MDTypography>
          
          {journeys && journeys.length > 0 ? (
            <MDBox display="flex" flexDirection="column">
              {journeys.map((j) => (
                <FormControlLabel
                  key={j.journeyId}
                  control={
                    <Checkbox
                      checked={!!selectedJourneys[j.journeyId]}
                      onChange={() => onToggleJourney(j.journeyId)}
                      sx={{
                        '&.Mui-checked': {
                          color: ({ palette: { info } }) => info.main,
                        },
                      }}
                    />
                  }
                  label={
                    <MDBox>
                      <MDTypography variant="button" fontWeight="medium" display="block">
                        {j.journeyName} ({j.type})
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        <Icon sx={{ fontSize: "12px !important", verticalAlign: "middle", mr: 0.5 }}>schedule</Icon>
                        {j.interval.startTime} - {j.interval.endTime}
                      </MDTypography>
                    </MDBox>
                  }
                  sx={{
                    mb: 0.5,
                    alignItems: "flex-start",
                    "& .MuiFormControlLabel-label": { mt: 0.5 }
                  }}
                />
              ))}
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="secondary" italic>
              No routes available for this vehicle.
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Default props for the component
VehicleCard.defaultProps = {
  journeys: [],
  selectedJourneys: {},
  capacityValue: 50,
};

// Prop types for validation
VehicleCard.propTypes = {
  title: PropTypes.string.isRequired,
  journeys: PropTypes.arrayOf(PropTypes.object),
  selectedJourneys: PropTypes.object,
  onToggleJourney: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCapacityChange: PropTypes.func.isRequired,
  capacityValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default VehicleCard;